/**
 * Shared Status Color Mappings
 * Consolidated color mappings for consistent status indicators across all components
 */

// Assignment Status Colors (used in LogisticsManager, GuestAsoebiTracker, etc.)
export const assignmentStatusColors: Record<string, string> = {
  pending: '#f59e0b',    // Orange
  confirmed: '#3b82f6',  // Blue
  completed: '#10b981',  // Green
  cancelled: '#ef4444',  // Red
};

// Movement Type Colors (used in LogisticsManager, ProjectTimeline, etc.)
export const movementTypeColors: Record<string, string> = {
  pickup: '#3b82f6',    // Blue
  dropoff: '#10b981',   // Green
  transfer: '#8b5cf6',  // Purple
};

// RSVP Status Colors (used in GuestManager, GuestAsoebiTracker, etc.)
export const rsvpStatusColors: Record<string, string> = {
  pending: '#f59e0b',    // Orange
  confirmed: '#10b981',  // Green
  tentative: '#3b82f6',  // Blue
  declined: '#ef4444',   // Red
};

// Event Category Colors (used in EventsList, CalendarView, etc.)
export const eventCategoryColors: Record<string, string> = {
  'Getting Ready': '#a78bfa',  // Purple
  'Ceremony': '#f472b6',       // Pink
  'Cocktail Hour': '#34d399',  // Green
  'Reception': '#60a5fa',      // Blue
  'After Party': '#fb923c',    // Orange
  'Rehearsal': '#94a3b8',      // Gray
};

// Priority Colors (used in various components for priority indicators)
export const priorityColors: Record<string, string> = {
  low: '#10b981',      // Green
  medium: '#f59e0b',    // Orange
  high: '#ef4444',      // Red
  urgent: '#dc2626',    // Dark Red
};

// Stock Status Colors (used in FabricCatalog, etc.)
export const stockStatusColors: Record<string, string> = {
  normal: '#10b981',   // Green
  low: '#f59e0b',       // Orange
  out: '#ef4444',       // Red
};

// Budget Status Colors (used in BudgetTracker)
export const budgetStatusColors: Record<string, string> = {
  under: '#10b981',    // Green
  on_track: '#f59e0b', // Orange
  over: '#ef4444',     // Red
};

// Vendor Task Status Colors (used in VendorBrief)
export const vendorTaskStatusColors: Record<string, string> = {
  'Not Started': '#6b7280',
  'In Progress': '#f59e0b',
  'Done':        '#10b981',
};

// Blocked is a facet independent of status now (see TasksService.ts
// BlockedOn) rather than a 4th status value — its own color map, keyed by
// blockedOn ('internal' | 'external'), used alongside vendorTaskStatusColors.
export const vendorTaskBlockedOnColors: Record<string, string> = {
  internal: '#ef4444',
  external: '#b91c1c',
};

// Project Timeline Status Colors (used in ProjectTimeline)
export const timelineStatusColors: Record<string, string> = {
  upcoming:      '#6b7280',
  'in-progress': '#f59e0b',
  completed:     '#10b981',
};

/**
 * Helper function to get status color with fallback
 */
export function getStatusColor(status: string, colorMap: Record<string, string>, fallback = '#6b7280'): string {
  return colorMap[status.toLowerCase()] || fallback;
}

/**
 * Helper function to get all colors for a specific domain
 */
export function getColorPalette(domain: keyof typeof colorPalettes): Record<string, string> {
  return colorPalettes[domain];
}

// Consolidated color palettes for different domains
const colorPalettes = {
  assignment: assignmentStatusColors,
  movement: movementTypeColors,
  rsvp: rsvpStatusColors,
  event: eventCategoryColors,
  priority: priorityColors,
  stock: stockStatusColors,
  budget: budgetStatusColors,
  vendorTask: vendorTaskStatusColors,
  timeline: timelineStatusColors,
} as const;
